import {
  Box,
  Button,
  Chip,
  InputAdornment,
  styled,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  Slide,
  IconButton,
  CircularProgress,
  Paper,
  Grid,
  Pagination,
  Popover,
  Stack,
  TextField,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { LocalizationProvider, StaticDateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";

import FlexBox from "components/FlexBox";
import useTitle from "hooks/useTitle";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { listSubmissions } from "redux/forms";
import { loggedInUser } from "utils/state";
import CloseIcon from "icons/CloseIcon";
import LightIconButton from "components/LightIconButton";
import PlusIcon from "icons/PlusIcon";
import ShareIcon from "icons/ShareIcon";
import PdfIcon from "icons/PdfIcon";
import moment from "moment";
import { H4, H6, Small, Tiny } from "components/Typography";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 500];

const INVOICE_CHIP: Record<string, { label: string; color: "default" | "warning" | "success" }> = {
  not_invoiced: { label: "Not invoiced", color: "default" },
  invoiced:     { label: "Invoiced",     color: "warning"  },
  paid:         { label: "Invoice paid", color: "success"  },
};

// commission color palette
const COMM_PAID_COLOR   = "#2CC5BD"; // teal  — commission exists
const COMM_UNPAID_COLOR = "#FD396D"; // red   — no commission (fraud risk)

// commission status filter
type CommFilter = "all" | "pending" | "awaiting" | "paid";
const COMM_FILTER_LABELS: Record<CommFilter, string> = {
  all:      "All",
  pending:  "Pending",
  awaiting: "Awaiting payment",
  paid:     "Paid",
};

// ─────────────────────────────────────────────────────────────────────────────
// Date range helpers
// ─────────────────────────────────────────────────────────────────────────────

const DATE_FMT = "YYYY-MM-DD";

type RangeKey = "today" | "yesterday" | "this_week" | "this_month" | "last_month";

const RANGE_LABELS: Record<RangeKey, string> = {
  today:      "Today",
  yesterday:  "Yesterday",
  this_week:  "This Week",
  this_month: "This Month",
  last_month: "Last Month",
};

function rangeForKey(key: RangeKey): { from: string; to: string } {
  const now = moment();
  switch (key) {
    case "today":
      return { from: now.format(DATE_FMT), to: now.format(DATE_FMT) };
    case "yesterday": {
      const y = moment().subtract(1, "day");
      return { from: y.format(DATE_FMT), to: y.format(DATE_FMT) };
    }
    case "this_week":
      return { from: moment().startOf("isoWeek").format(DATE_FMT), to: now.format(DATE_FMT) };
    case "this_month":
      return { from: moment().startOf("month").format(DATE_FMT), to: now.format(DATE_FMT) };
    case "last_month":
      return {
        from: moment().subtract(1, "month").startOf("month").format(DATE_FMT),
        to:   moment().subtract(1, "month").endOf("month").format(DATE_FMT),
      };
  }
}

const DEFAULT_RANGE: RangeKey = "this_month";

// ─────────────────────────────────────────────────────────────────────────────
// Styled helpers
// ─────────────────────────────────────────────────────────────────────────────

const StyledFlexBox = styled(FlexBox)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: 20,
  [theme.breakpoints.down(500)]: {
    width: "100%",
    "& .MuiButton-root": { width: "100%", marginTop: 15 },
  },
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: theme.palette.text.disabled,
  marginBottom: 8,
}));

const DetailCard = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  padding: "16px 20px",
  marginBottom: 12,
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#F9FAFB",
  boxShadow: "none",
  border: `1px solid ${theme.palette.divider}`,
}));

const FieldRow: FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <Box mb={1.5}>
    <Typography variant="caption" sx={{ color: "text.disabled", fontWeight: 600, display: "block" }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Request card (replaces table row)
// ─────────────────────────────────────────────────────────────────────────────

const RequestCard: FC<{ row: any; onClick: () => void }> = ({ row, onClick }) => {
  const commissionExists: boolean = !!row.commission_exists;
  const commissionPaid:   boolean = !!row.commission_paid;
  const accentColor = commissionPaid   ? COMM_PAID_COLOR
                    : commissionExists ? "#FF9800"          // amber — exists but unpaid
                    :                   COMM_UNPAID_COLOR;  // red   — not yet created
  const bgColor     = commissionPaid   ? "rgba(44,197,189,0.06)"
                    : commissionExists ? "rgba(255,152,0,0.05)"
                    :                   "rgba(253,57,109,0.05)";
  const invoiceStatus = row.invoice_status ?? "not_invoiced";
  const invoiceChip   = INVOICE_CHIP[invoiceStatus] ?? INVOICE_CHIP.not_invoiced;

  return (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        position: "relative",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        borderLeft: `4px solid ${accentColor}`,
        backgroundColor: bgColor,
        p: 2,
        mb: 1.5,
        cursor: "pointer",
        transition: "box-shadow 0.15s ease, transform 0.1s ease",
        "&:hover": {
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          transform: "translateY(-1px)",
        },
        "&:active": { transform: "translateY(0)" },
      }}
    >
      {/* Top row */}
      <FlexBox justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box>
          <H6 color="text.primary" sx={{ fontWeight: 700 }}>{row.customer_names}</H6>
          <Tiny color="text.disabled">{row.customer_phone_number}</Tiny>
        </Box>
        <Box
          sx={{
            borderRadius: 10,
            px: 1.5,
            py: 0.25,
            backgroundColor: "#A798FF",
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            whiteSpace: "nowrap",
            ml: 1,
          }}
        >
          {row.reg_no}
        </Box>
      </FlexBox>

      {/* Middle row — partner + date */}
      <FlexBox justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box>
          <Tiny color="text.primary" sx={{ fontWeight: 600 }}>{row.partner?.names}</Tiny>
          <Tiny color="text.disabled">{row.partner_branch?.name}</Tiny>
        </Box>
        <Tiny color="text.disabled">
          {row.created_at ? moment(row.created_at).format("DD MMM YYYY") : "—"}
        </Tiny>
      </FlexBox>

      {/* Amounts row */}
      <FlexBox gap={2} mb={1.5}>
        <Box>
          <Tiny color="text.disabled" sx={{ fontWeight: 600 }}>INV</Tiny>
          <Tiny color="text.primary" sx={{ fontWeight: 700 }}>KShs {row.invoice_amount ?? "—"}</Tiny>
        </Box>
        <Box>
          <Tiny color="text.disabled" sx={{ fontWeight: 600 }}>COMM</Tiny>
          <Tiny color="text.primary" sx={{ fontWeight: 700 }}>KShs {row.commission_amount ?? "—"}</Tiny>
        </Box>
        {row.invoice_grand_total && (
          <Box>
            <Tiny color="text.disabled" sx={{ fontWeight: 600 }}>BILLED</Tiny>
            <Tiny color="text.primary" sx={{ fontWeight: 700 }}>KShs {row.invoice_grand_total}</Tiny>
          </Box>
        )}
      </FlexBox>

      {/* Status chips */}
      <FlexBox gap={1} flexWrap="wrap">
        <Chip size="small" label={invoiceChip.label} color={invoiceChip.color} sx={{ fontSize: 10, height: 20 }} />
        <Chip
          size="small"
          label={
            commissionPaid   ? "✓ Commission paid" :
            commissionExists ? "Comm. awaiting payment" :
                               "Commission pending"
          }
          sx={{
            fontSize: 10,
            height: 20,
            fontWeight: 600,
            backgroundColor: commissionPaid
              ? "rgba(44,197,189,0.18)"
              : commissionExists
                ? "rgba(255,152,0,0.18)"
                : "rgba(253,57,109,0.15)",
            color: commissionPaid ? "#1a8c86" : commissionExists ? "#b36a00" : "#c0224a",
          }}
        />
        <Chip
          size="small"
          label={row.status}
          variant="outlined"
          sx={{ fontSize: 10, height: 20, textTransform: "capitalize" }}
        />
      </FlexBox>
    </Paper>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Diagonal watermark overlay
// ─────────────────────────────────────────────────────────────────────────────

const CommissionWatermark: FC = () => (
  <Box
    aria-hidden
    sx={{
      position: "fixed",
      inset: 0,
      pointerEvents: "none",
      zIndex: 1400,           // above Dialog content (Dialog is 1300)
      overflow: "hidden",
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gridTemplateRows: "repeat(10, 1fr)",
    }}
  >
    {Array.from({ length: 30 }).map((_, i) => (
      <FlexBox
        key={i}
        alignItems="center"
        justifyContent="center"
        sx={{ overflow: "hidden" }}
      >
        <Typography
          sx={{
            transform: "rotate(-38deg)",
            color: "rgba(44,197,189,0.13)",
            fontWeight: 900,
            fontSize: 13,
            letterSpacing: 2,
            userSelect: "none",
            whiteSpace: "nowrap",
          }}
        >
          ✓ COMMISSION PAID
        </Typography>
      </FlexBox>
    ))}
  </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Transition — must be defined outside the component to keep a stable
// reference; defining it inside causes Dialog to unmount/remount every render.
// ─────────────────────────────────────────────────────────────────────────────

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) { return <Slide direction="up" ref={ref} {...props} />; });

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

const ListFormsFOInstallationRequests: FC = () => {
  useTitle("Installation requests");

  const user = loggedInUser();
  const navigate = useNavigate();

  const initialRange = rangeForKey(DEFAULT_RANGE);

  const [state,        setState]        = useState("loading");
  const [open,         setOpen]         = useState(false);
  const [curViewRow,   setCurViewRow]   = useState<any>(null);
  const [rows,         setRows]         = useState<any[]>([]);
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(DEFAULT_PAGE_SIZE);
  const [totalCount,   setTotalCount]   = useState(0);
  const [currentRange, setCurrentRange] = useState(initialRange);
  const [activeChip,   setActiveChip]   = useState<RangeKey | null>(DEFAULT_RANGE);
  const [pickerRange,  setPickerRange]  = useState<[Date | null, Date | null]>([
    new Date(initialRange.from),
    new Date(initialRange.to),
  ]);
  const [pickerAnchor, setPickerAnchor] = useState<HTMLButtonElement | null>(null);
  const [commFilter,   setCommFilter]   = useState<CommFilter>("all");
  const [search,       setSearch]       = useState("");

  // client-side filter: commission + search applied to the current page
  const visibleRows = useMemo(() => {
    let r = rows;

    if (commFilter !== "all") {
      r = r.filter((row) => {
        if (commFilter === "pending")  return !row.commission_exists;
        if (commFilter === "awaiting") return row.commission_exists && !row.commission_paid;
        if (commFilter === "paid")     return !!row.commission_paid;
        return true;
      });
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      r = r.filter((row) =>
        [row.customer_names, row.reg_no, row.agent_names, row.partner?.names]
          .some((v) => v && String(v).toLowerCase().includes(q))
      );
    }

    return r;
  }, [rows, commFilter, search]);

  // page count driven by server total
  const pageCount = Math.ceil(totalCount / pageSize);

  const handleRowClicked  = (row: any) => { setOpen(true); setCurViewRow(row); };
  const handleCloseDrawer = ()         => { setOpen(false); setCurViewRow(null); };

  const fetchData = useCallback((from: string, to: string, pg: number = 1, limit: number = pageSize) => {
    let cancelled = false;
    setState("loading");
    setCurrentRange({ from, to });
    const offset = (pg - 1) * limit;
    listSubmissions({
      params: { date_filter: `${from}--${to}`, limit, offset },
      url: `v1/tracking/account-requests/`,
    })
      .then((response: any) => {
        if (cancelled) return;
        if (response?.status === 200) {
          setRows(response.data.results ?? []);
          setTotalCount(response.data.count ?? 0);
        } else toast.error("Something went wrong.");
        setState("loaded");
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(error);
        setState("loaded");
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    return fetchData(initialRange.from, initialRange.to, 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (_: any, p: number) => {
    setPage(p);
    fetchData(currentRange.from, currentRange.to, p, pageSize);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
    fetchData(currentRange.from, currentRange.to, 1, newSize);
  };

  const handleChipClick = (key: RangeKey) => {
    const { from, to } = rangeForKey(key);
    setPickerRange([new Date(from), new Date(to)]);
    setActiveChip(key);
    setPage(1);
    fetchData(from, to, 1);
  };

  const handlePickerApply = () => {
    if (pickerRange[0] && pickerRange[1]) {
      const from = moment(pickerRange[0]).format(DATE_FMT);
      const to   = moment(pickerRange[1]).format(DATE_FMT);
      setActiveChip(null);
      setPage(1);
      fetchData(from, to, 1);
    }
    setPickerAnchor(null);
  };

  const handleCommFilter = (val: CommFilter) => {
    setCommFilter(val);
    setPage(1);
  };

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(1);
  };

  const rangeLabel = (() => {
    const [s, e] = pickerRange;
    if (!s || !e) return "Select dates";
    const sStr = moment(s).format(DATE_FMT);
    const eStr = moment(e).format(DATE_FMT);
    if (sStr === eStr) return moment(s).format("DD MMM YYYY");
    return `${moment(s).format("DD MMM")} – ${moment(e).format("DD MMM YYYY")}`;
  })();

  // ── Share ────────────────────────────────────────────────────────
  const buildShareText = (obj: any) => {
    if (!obj) return "";
    const commLine = obj.commission_exists
      ? "✓ Commission has been registered"
      : "⚠ Commission NOT yet registered";
    return [
      `*Installation request — ${obj.created_by}*`,
      `Date: ${obj.created_at ? moment(obj.created_at).format("DD MMM YYYY HH:mm") : "None"}`,
      "",
      `*Customer*`,
      `${obj.customer_names}  ${obj.customer_phone_number}`,
      "",
      `Make: ${obj.motor_make}   Reg: ${obj.reg_no}`,
      `Bank: ${obj.partner?.names ?? ""}, ${obj.partner_branch?.name ?? ""}`,
      "",
      `Invoice: KShs ${obj.invoice_amount}   Commission: KShs ${obj.commission_amount}`,
      commLine,
      "",
      `Agent: ${obj.agent_names} (${obj.agent_mpesa_number})`,
      `Contact: ${obj.contact_person_names} (${obj.contact_person_phone_number})`,
      `Place: ${obj.place_of_installation}`,
      `Duration: ${obj.duration} months`,
      obj.notes ? `Notes: ${obj.notes}` : "",
      "",
      `*Posted by ${user?.first_name} ${user?.last_name}*`,
    ].filter((l) => l !== undefined).join("\n");
  };

  const handleShare = async () => {
    const message = buildShareText(curViewRow);
    if (navigator.share) {
      try {
        await navigator.share({ title: `Installation request`, text: message });
        toast.success("Shared successfully.");
      } catch { toast.error("Share cancelled."); }
    } else {
      window.open(`https://web.whatsapp.com/send?text=${encodeURIComponent(message)}`);
    }
  };

  // ── Dialog computed values ────────────────────────────────────────
  const commissionExists: boolean = !!curViewRow?.commission_exists;
  const commissionPaid:   boolean = !!curViewRow?.commission_paid;
  const invoiceStatus = curViewRow?.invoice_status ?? "not_invoiced";
  const invoiceChip   = INVOICE_CHIP[invoiceStatus] ?? INVOICE_CHIP.not_invoiced;

  // ── Render ────────────────────────────────────────────────────────
  return (
    <Box pt={2} pb={4}>
      {/* Top bar — create button + count */}
      <StyledFlexBox>
        <LightIconButton
          onClick={() => navigate("/dashboard/forms/fo/installation/request/new")}
          size="small"
        >
          <PlusIcon fontSize="small" />
          Create
        </LightIconButton>
        {totalCount > 0 && (
          <Tiny color="text.disabled">
            {totalCount} request{totalCount !== 1 ? "s" : ""}
          </Tiny>
        )}
      </StyledFlexBox>

      {/* ── Filter toolbar ── */}
      <Box mb={2}>

        {/* Date range quick chips */}
        <Stack direction="row" flexWrap="wrap" gap={0.75} mb={1}>
          {(Object.keys(RANGE_LABELS) as RangeKey[]).map((key) => (
            <Chip
              key={key}
              label={RANGE_LABELS[key]}
              size="small"
              onClick={() => handleChipClick(key)}
              color={activeChip === key ? "primary" : "default"}
              variant={activeChip === key ? "filled" : "outlined"}
              sx={{ fontSize: 11, height: 24 }}
            />
          ))}
        </Stack>

        {/* Date range trigger button */}
        <Button
          variant="outlined"
          size="small"
          fullWidth
          onClick={(e) => setPickerAnchor(e.currentTarget)}
          sx={{
            textTransform: "none",
            justifyContent: "space-between",
            fontSize: 13,
            fontWeight: 600,
            color: "text.primary",
            borderColor: "text.disabled",
            borderRadius: 2,
            px: 1.5,
            py: 0.75,
            mb: 1.5,
            "&:hover": { borderColor: "text.primary", backgroundColor: "action.hover" },
          }}
          endIcon={<Typography sx={{ fontSize: 16, lineHeight: 1 }}>📅</Typography>}
        >
          {rangeLabel}
        </Button>

        {/* Date range popover */}
        <Popover
          open={Boolean(pickerAnchor)}
          anchorEl={pickerAnchor}
          onClose={() => setPickerAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StaticDateRangePicker
              displayStaticWrapperAs="mobile"
              value={pickerRange}
              onChange={(newVal: any) => setPickerRange(newVal)}
              maxDate={new Date()}
              renderInput={() => <></>}
            />
          </LocalizationProvider>
          <Box sx={{ px: 2, pb: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button size="small" onClick={() => setPickerAnchor(null)}>Cancel</Button>
            <Button
              size="small"
              variant="contained"
              disabled={!pickerRange[0] || !pickerRange[1]}
              onClick={handlePickerApply}
            >
              Apply
            </Button>
          </Box>
        </Popover>

        {/* Commission status filter */}
        <Stack direction="row" flexWrap="wrap" gap={0.75} mb={1.5}>
          {(Object.keys(COMM_FILTER_LABELS) as CommFilter[]).map((key) => {
            const isActive = commFilter === key;
            const colorMap: Record<CommFilter, string> = {
              all:      "#A798FF",
              pending:  COMM_UNPAID_COLOR,
              awaiting: "#FF9800",
              paid:     COMM_PAID_COLOR,
            };
            return (
              <Chip
                key={key}
                label={COMM_FILTER_LABELS[key]}
                size="small"
                onClick={() => handleCommFilter(key)}
                sx={{
                  fontSize: 11,
                  height: 24,
                  fontWeight: isActive ? 700 : 400,
                  backgroundColor: isActive ? colorMap[key] : "transparent",
                  color: isActive ? "#000" : "text.secondary",
                  border: `1px solid ${isActive ? colorMap[key] : "rgba(0,0,0,0.2)"}`,
                  "&:hover": { backgroundColor: isActive ? colorMap[key] : "action.hover" },
                }}
              />
            );
          })}
        </Stack>

        {/* Search */}
        <TextField
          size="small"
          fullWidth
          placeholder="Search customer, reg no, agent, bank…"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography sx={{ fontSize: 15 }}>🔍</Typography>
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleSearch("")}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
        />

      </Box>

      {state === "loading" ? (
        <FlexBox justifyContent="center" mt={6}>
          <CircularProgress />
        </FlexBox>
      ) : visibleRows.length === 0 ? (
        <H4 color="text.disabled" sx={{ textAlign: "center", mt: 6 }}>
          {rows.length === 0 ? "No requests yet" : "No results match your filters"}
        </H4>
      ) : (
        <>
          {visibleRows.map((row: any) => (
            <RequestCard key={row.id ?? row.code} row={row} onClick={() => handleRowClicked(row)} />
          ))}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={1}>
            {/* Per-page selector */}
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Tiny color="text.disabled">Per page:</Tiny>
              {PAGE_SIZE_OPTIONS.map((n) => (
                <Chip
                  key={n}
                  label={n}
                  size="small"
                  onClick={() => handlePageSizeChange(n)}
                  color={pageSize === n ? "primary" : "default"}
                  variant={pageSize === n ? "filled" : "outlined"}
                  sx={{ fontSize: 11, height: 22, minWidth: 34 }}
                />
              ))}
            </Stack>

            {/* Page navigation */}
            {pageCount > 1 && (
              <Pagination
                count={pageCount}
                page={page}
                shape="rounded"
                onChange={handlePageChange}
              />
            )}
          </Stack>
        </>
      )}

      {/* ── Detail dialog ── */}
      <Dialog
        open={open}
        onClose={handleCloseDrawer}
        TransitionComponent={Transition}
        fullScreen
        PaperProps={{ sx: { backgroundColor: "background.default" } }}
      >
        {/* Watermark — only when commission has been paid */}
        {commissionPaid && <CommissionWatermark />}

        {/* App bar */}
        <AppBar
          sx={{ position: "sticky", top: 0, backgroundColor: "background.paper", boxShadow: 1, zIndex: 1500 }}
          elevation={0}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <FlexBox alignItems="center" gap={1}>
              <LightIconButton
                size="small"
                onClick={() =>
                  navigate(`/dashboard/forms/fo/installation/request/details/pdf/?cert_link=${curViewRow?.cert_pdf_link ?? ""}`)
                }
              >
                <PdfIcon />
              </LightIconButton>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "text.primary" }}>
                Request details
              </Typography>
            </FlexBox>
            <FlexBox gap={1}>
              <IconButton size="small" onClick={handleShare}><ShareIcon /></IconButton>
              <IconButton size="small" onClick={handleCloseDrawer}><CloseIcon /></IconButton>
            </FlexBox>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2, maxWidth: 600, mx: "auto", width: "100%", position: "relative", zIndex: 1 }}>

          {/* Payment status banner */}
          <Box
            sx={{
              borderRadius: 3,
              p: 2,
              mb: 2,
              background: commissionExists
                ? "linear-gradient(135deg, rgba(44,197,189,0.18) 0%, rgba(44,197,189,0.06) 100%)"
                : "linear-gradient(135deg, rgba(253,57,109,0.14) 0%, rgba(253,57,109,0.04) 100%)",
              border: `1px solid ${commissionExists ? "rgba(44,197,189,0.4)" : "rgba(253,57,109,0.35)"}`,
            }}
          >
            <SectionLabel sx={{ mb: 1.5 }}>Payment status</SectionLabel>
            <FlexBox gap={1} flexWrap="wrap" mb={1}>
              <Chip size="small" label={invoiceChip.label} color={invoiceChip.color} sx={{ fontWeight: 600, fontSize: 11 }} />
              {curViewRow?.invoice_no && (
                <Chip size="small" variant="outlined" label={curViewRow.invoice_no} sx={{ fontSize: 11 }} />
              )}
              {curViewRow?.invoice_grand_total && (
                <Chip size="small" variant="outlined" label={`KShs ${curViewRow.invoice_grand_total}`} sx={{ fontSize: 11 }} />
              )}
            </FlexBox>
            <FlexBox gap={1} flexWrap="wrap" alignItems="center">
              <Chip
                size="small"
                label={
                  commissionPaid   ? "✓ Commission paid" :
                  commissionExists ? "Commission registered — awaiting payment" :
                                     "⚠ Commission pending"
                }
                sx={{
                  fontWeight: 700,
                  fontSize: 11,
                  backgroundColor: commissionPaid
                    ? "rgba(44,197,189,0.22)"
                    : commissionExists
                      ? "rgba(255,152,0,0.18)"
                      : "rgba(253,57,109,0.18)",
                  color: commissionPaid ? "#0e7370" : commissionExists ? "#b36a00" : "#c0224a",
                }}
              />
              {!commissionExists && (
                <Typography variant="caption" sx={{ color: "error.main", fontWeight: 600 }}>
                  Not yet registered
                </Typography>
              )}
            </FlexBox>
          </Box>

          {/* Customer */}
          <DetailCard>
            <SectionLabel>Customer</SectionLabel>
            <Grid container spacing={1}>
              <Grid item xs={6}><FieldRow label="Name"  value={curViewRow?.customer_names} /></Grid>
              <Grid item xs={6}><FieldRow label="Phone" value={curViewRow?.customer_phone_number} /></Grid>
            </Grid>
          </DetailCard>

          {/* Vehicle */}
          <DetailCard>
            <SectionLabel>Vehicle</SectionLabel>
            <Grid container spacing={1}>
              <Grid item xs={6}><FieldRow label="Reg no" value={curViewRow?.reg_no} /></Grid>
              <Grid item xs={6}><FieldRow label="Make"   value={curViewRow?.motor_make} /></Grid>
              <Grid item xs={12}><FieldRow label="Place of installation" value={curViewRow?.place_of_installation} /></Grid>
            </Grid>
          </DetailCard>

          {/* Partner */}
          <DetailCard>
            <SectionLabel>Partner / Bank</SectionLabel>
            <Grid container spacing={1}>
              <Grid item xs={6}><FieldRow label="Bank"     value={curViewRow?.partner?.names} /></Grid>
              <Grid item xs={6}><FieldRow label="Branch"   value={curViewRow?.partner_branch?.name} /></Grid>
              <Grid item xs={6}><FieldRow label="Duration" value={curViewRow?.duration ? `${curViewRow.duration} months` : null} /></Grid>
              <Grid item xs={6}><FieldRow label="Status"   value={curViewRow?.status} /></Grid>
            </Grid>
          </DetailCard>

          {/* Financial */}
          <DetailCard>
            <SectionLabel>Financial</SectionLabel>
            <Grid container spacing={1}>
              <Grid item xs={6}><FieldRow label="Expected invoice (KShs)"    value={curViewRow?.invoice_amount} /></Grid>
              <Grid item xs={6}><FieldRow label="Expected commission (KShs)" value={curViewRow?.commission_amount} /></Grid>
            </Grid>
          </DetailCard>

          {/* Agent */}
          <DetailCard>
            <SectionLabel>Agent / Dealer</SectionLabel>
            <Grid container spacing={1}>
              <Grid item xs={6}><FieldRow label="Name"     value={curViewRow?.agent_names} /></Grid>
              <Grid item xs={6}><FieldRow label="M-Pesa no" value={curViewRow?.agent_mpesa_number} /></Grid>
            </Grid>
          </DetailCard>

          {/* Contact */}
          <DetailCard>
            <SectionLabel>Contact person</SectionLabel>
            <Grid container spacing={1}>
              <Grid item xs={6}><FieldRow label="Name"  value={curViewRow?.contact_person_names} /></Grid>
              <Grid item xs={6}><FieldRow label="Phone" value={curViewRow?.contact_person_phone_number} /></Grid>
            </Grid>
          </DetailCard>

          {/* Notes */}
          {curViewRow?.notes && (
            <DetailCard>
              <SectionLabel>Notes</SectionLabel>
              <Typography variant="body2" sx={{ color: "text.primary", whiteSpace: "pre-wrap" }}>
                {curViewRow.notes}
              </Typography>
            </DetailCard>
          )}

          {/* Meta */}
          <Box sx={{ px: 1, pb: 4 }}>
            <Typography variant="caption" color="text.disabled">
              Submitted by {curViewRow?.created_by} ·{" "}
              {curViewRow?.created_at ? moment(curViewRow.created_at).format("DD MMM YYYY, HH:mm") : ""}
            </Typography>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ListFormsFOInstallationRequests;
